import Decimal from 'decimal.js';

import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { formatMoney } from '../../util/currency';
import { convertMoneyToCurrency } from '../../util/currencyConversion';
import { richText } from '../../util/richText';
import { types } from '../../util/sdkLoader';
import { isBookingProcessAlias } from '../../transactions/transaction';

import css from './ListingCard.module.css';

const { Money } = types;

/**
 * @param {unknown} value
 * @returns {import('sharetribe-flex-sdk').Money | null}
 */
const coerceMoney = value => {
  if (!value) {
    return null;
  }
  if (value instanceof Money) {
    return value;
  }
  if (typeof value === 'object' && value.amount != null && value.currency) {
    try {
      return new Money(value.amount, value.currency);
    } catch {
      return null;
    }
  }
  return null;
};

const moneyAmountString = m => {
  if (m == null || m.amount == null) {
    return '0';
  }
  return typeof m.amount.toString === 'function' ? m.amount.toString() : String(m.amount);
};

/**
 * @param {import('sharetribe-flex-sdk').Money | null | undefined} price
 * @param {Object} publicData
 * @returns {{ showSale: boolean, compareAt: import('sharetribe-flex-sdk').Money | null, saveMoney: import('sharetribe-flex-sdk').Money | null }}
 */
const getSaleMeta = (price, publicData) => {
  if (!price || !(price instanceof Money)) {
    return { showSale: false, compareAt: null, saveMoney: null };
  }
  const compareRaw =
    publicData?.compareAtPrice || publicData?.originalPrice || publicData?.compareAt;
  const compareAt = coerceMoney(compareRaw);
  if (!compareAt || compareAt.currency !== price.currency) {
    return { showSale: false, compareAt: null, saveMoney: null };
  }
  const original = new Decimal(moneyAmountString(compareAt));
  const current = new Decimal(moneyAmountString(price));
  if (!original.gt(current)) {
    return { showSale: false, compareAt: null, saveMoney: null };
  }
  const saveSubUnits = original.minus(current);
  const saveInt = parseInt(saveSubUnits.toFixed(0), 10);
  return {
    showSale: true,
    compareAt,
    saveMoney: new Money(saveInt, price.currency),
  };
};

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl, config) => {
  if (price) {
    const convertedPrice = convertMoneyToCurrency(price, currency, config);
    const moneyToDisplay = convertedPrice || price;

    if (moneyToDisplay) {
      try {
        const formattedPrice = formatMoney(intl, moneyToDisplay);
        return { formattedPrice, priceTooltip: formattedPrice };
      } catch (e) {
        // Fall through to unsupported currency copy if money can't be formatted.
      }
    }

    return {
      formattedPrice: intl.formatMessage({ id: 'ListingCard.unsupportedPrice' }, { currency: price.currency }),
      priceTooltip: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

/**
 * Returns all translated and formatted strings for ListingCard so the
 * presentational component can stay simple and aria-labels use the same copy.
 *
 * @param {Object} listing - API entity: listing or ownListing
 * @param {Object} config - app configuration (e.g. from useConfiguration())
 * @param {Object} intl - React Intl instance (e.g. from useIntl())
 * @returns {Object} translations and derived values:
 *   - titlePlain: raw title string (for aria/alt)
 *   - titleFormatted: React nodes from richText(title) for display
 *   - showPrice: whether to show the price block
 *   - priceTooltip: string for the price element's title attribute (tooltip on hover)
 *   - priceMessage: string or null for the price block content (same translation as used in cardAriaLabel when shown)
 *   - cardAriaLabel: ready-to-use aria-label for the card link (listing title + price line when shown)
 *   - authorName: "ListingCard.author" string containing author's display name
 */
export const getListingCardTranslations = (listing, config, intl) => {
  const { title = '', price, publicData } = listing?.attributes || {};

  const authorDisplayName = listing?.author?.attributes?.profile?.displayName;
  const authorName = intl.formatMessage(
    { id: 'ListingCard.author' },
    { authorName: authorDisplayName }
  );

  const validListingTypes = config.listing.listingTypes || [];
  const { listingType } = publicData || {};
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(listingTypeConfig);
  const { formattedPrice, priceTooltip } = priceData(price, config.currency, intl, config);

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  const priceMessageId = hasMultiplePriceVariants
    ? 'ListingCard.priceStartingFrom'
    : 'ListingCard.price';

  const perUnitString = isBookable
    ? intl.formatMessage({ id: 'ListingCard.perUnit' }, { unitType: publicData?.unitType })
    : '';

  const { showSale, compareAt, saveMoney } = getSaleMeta(price, publicData || {});
  const convertedCompareAt = compareAt ? convertMoneyToCurrency(compareAt, config.currency, config) : null;
  const convertedSaveMoney = saveMoney ? convertMoneyToCurrency(saveMoney, config.currency, config) : null;
  let formattedCompareAt = null;
  let formattedSave = null;
  if (showSale && (convertedCompareAt || compareAt)) {
    try {
      formattedCompareAt = formatMoney(intl, convertedCompareAt || compareAt);
    } catch (e) {
      formattedCompareAt = null;
    }
  }
  if (showSale && (convertedSaveMoney || saveMoney)) {
    try {
      formattedSave = formatMoney(intl, convertedSaveMoney || saveMoney);
    } catch (e) {
      formattedSave = null;
    }
  }

  const priceValue = <span className={css.currentPriceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? <span className={css.pricePerUnit}>{perUnitString}</span> : '';
  const priceMessage =
    showPrice && formattedPrice != null
      ? intl.formatMessage({ id: priceMessageId }, { priceValue, pricePerUnit })
      : '';

  const rawRating = publicData?.cardRating ?? publicData?.rating;
  const ratingValue =
    typeof rawRating === 'number' && !Number.isNaN(rawRating)
      ? Math.min(5, Math.max(0, rawRating))
      : null;
  const showRatingRow = ratingValue != null && ratingValue > 0;
  const roundedStarRating = Math.min(5, Math.max(0, Math.round(ratingValue)));
  const reviewCountRaw = publicData?.reviewCount ?? publicData?.reviewNum;
  const reviewCount =
    typeof reviewCountRaw === 'number' && reviewCountRaw >= 0 ? reviewCountRaw : null;

  let cardAriaLabel = title;
  if (showPrice && formattedPrice != null) {
    const priceForAria = showSale
      ? intl.formatMessage(
          { id: 'ListingCard.screenreader.priceOnSale' },
          {
            current: formattedPrice,
            original: formattedCompareAt,
            save: formattedSave,
            perUnit: perUnitString ? ` ${perUnitString}` : '',
          }
        )
      : intl.formatMessage(
          { id: 'ListingCard.screenreader.pricePlain' },
          { price: formattedPrice, perUnit: perUnitString ? ` ${perUnitString}` : '' }
        );
    cardAriaLabel = intl.formatMessage(
      { id: 'ListingCard.screenreader.label' },
      { listingTitle: title, formattedPrice: priceForAria }
    );
  }
  if (showRatingRow) {
    const reviewSuffix =
      reviewCount != null
        ? ` ${intl.formatMessage({ id: 'ListingCard.screenreader.reviewCount' }, { count: reviewCount })}`
        : '';
    cardAriaLabel = intl.formatMessage(
      { id: 'ListingCard.screenreader.withRating' },
      {
        priorLabel: cardAriaLabel,
        rating: ratingValue.toFixed(1),
        reviewSuffix,
      }
    );
  }

  return {
    titlePlain: title,
    titleFormatted: richText(title, {
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
    }),
    authorName,
    showPrice,
    priceTooltip,
    priceMessage,
    cardAriaLabel,
    showSale,
    formattedCompareAt,
    formattedSave,
    showRatingRow,
    ratingValue,
    roundedStarRating,
    reviewCount,
  };
};
